import { Stack, Typography } from "@mui/material";
import {
  EMPTY_CHARACTER,
  getIconProps,
  ICON_SIZE,
} from "@pulse/shared-components";
import {
  Banner,
  Button,
  Download,
  Spacing,
  Typography as LeoTypography,
  FoundationColorTokens,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React from "react";
import { UploadValidationLoader } from "../../../../components/fileUpload/UploadValidationLoader";
import { Instance } from "mobx-state-tree";
import { AddMultipleSPoCsStore } from "../../store/AddMultipleSPoCsStore";
import {
  NavigateToFunctions,
  processProjectParams,
} from "../../../../routes/RoutesHelper";
import { TFunction } from "i18next";
import {
  MAX_BULK_SPOC_FILE_SIZE_IN_BYTES,
  MIN_BULK_SPOC_FILE_SIZE_IN_BYTES,
} from "../../../utils/constants";
import { AlertCircle } from "lucide-react";
import { FileUploadComponent } from "../../../../components/fileUpload/FileUploadComponent";
import { getUploadErrorDescriptions } from "../../../utils/FileUploadUtils";
import { ValidateCSVAndUploadSpocsRPC } from "@pulse/pulse-rpcs/lib/spoc-management/validateCsvAndUploadSpocsRPC";

interface SPoCsUploadPaneProps {
  navigateTo: NavigateToFunctions;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  addMultipleSPoCsStore: Instance<typeof AddMultipleSPoCsStore>;
}

const SPoCsUploadPane = observer(
  ({
    spacing,
    t,
    typography,
    addMultipleSPoCsStore,
    tokens,
    navigateTo,
  }: SPoCsUploadPaneProps): React.ReactElement => {
    const fileUploadHandler = async (file: File): Promise<void> => {
      addMultipleSPoCsStore.setFileName(file.name);
      addMultipleSPoCsStore.resetRPCError();
      await processProjectParams(async (projectId) => {
        addMultipleSPoCsStore.setIsRPCLoading(true);
        await addMultipleSPoCsStore.uploadCSVFile(file, projectId);
        await addMultipleSPoCsStore.validateCSVAndUploadSPoCs(projectId);
        addMultipleSPoCsStore.setIsRPCLoading(false);
      }, navigateTo);
    };
    const UploadErrors = (): React.ReactElement => {
      const getErrorDescriptions = (): String => {
        if (addMultipleSPoCsStore.validationError) {
          return getUploadErrorDescriptions(
            addMultipleSPoCsStore.validationError,
            t,
            MAX_BULK_SPOC_FILE_SIZE_IN_BYTES,
            MIN_BULK_SPOC_FILE_SIZE_IN_BYTES,
          );
        }
        return addMultipleSPoCsStore.invalidFileDetails ?? EMPTY_CHARACTER;
      };

      const iconProps = getIconProps(tokens.iconError, ICON_SIZE.default);
      return (
        <Stack spacing={spacing.spaceXXS}>
          <Typography sx={{ ...typography.b2, color: tokens.label }}>
            {addMultipleSPoCsStore.fileName}
          </Typography>
          <Stack gap={spacing.spaceXXS} direction="row">
            {!addMultipleSPoCsStore.invalidFileDetails && (
              <AlertCircle {...iconProps} />
            )}
            <Typography
              sx={{
                ...typography.b2,
                color: tokens.labelError,
                whiteSpace: "pre-line",
              }}
            >
              {getErrorDescriptions()}
            </Typography>
          </Stack>
        </Stack>
      );
    };

    return (
      <Stack spacing={spacing.spaceMD}>
        <FileUploadComponent fileUploadHandler={fileUploadHandler} />
        {addMultipleSPoCsStore.doesStoreContainValidationError && (
          <UploadErrors />
        )}
      </Stack>
    );
  },
);

interface AddMultipleSPoCsDialogChildProps {
  addMultipleSPoCsStore: Instance<typeof AddMultipleSPoCsStore>;
  navigateTo: NavigateToFunctions;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
}

export const AddMultipleSPoCsDialogChild = observer(
  ({
    addMultipleSPoCsStore,
    t,
    spacing,
    tokens,
    typography,
    navigateTo,
  }: AddMultipleSPoCsDialogChildProps): React.ReactElement => {
    const getErrorBannerText = (): string | undefined => {
      if (addMultipleSPoCsStore.validationError) {
        return t("common.fileUploadErrorMessage.invalidFileFormat");
      }
      switch (addMultipleSPoCsStore.rpcError) {
        case ValidateCSVAndUploadSpocsRPC.RPCError.InvalidFile: {
          return t("common.fileUploadErrorMessage.invalidFileFormat");
        }
        default: {
          return undefined;
        }
      }
    };

    return (
      <Stack gap={spacing.spaceLG}>
        {getErrorBannerText() && (
          <Banner
            message={getErrorBannerText() ?? EMPTY_CHARACTER}
            severity="error"
            width="496px"
          />
        )}
        <Stack gap={spacing.spaceSM}>
          <Typography {...typography.b2} color={tokens.labelSubtle}>
            {t("projects.userAccess.addMultipleSPoCsDialog.description")}
          </Typography>
          <Stack direction={"row"} gap={spacing.spaceSM}>
            <Button
              name="primaryDownload"
              label={t("common.downloadTemplate")}
              icon={<Download />}
              iconPosition="leading"
              size="small"
              variant="outlined-neutral"
              loading={addMultipleSPoCsStore.isDownloadTemplateButtonLoading}
              onClick={async (): Promise<void> => {
                await addMultipleSPoCsStore.downloadBulkSPoCUploadTemplate();
              }}
            />
          </Stack>
        </Stack>
        {addMultipleSPoCsStore.isRPCLoading ? (
          <UploadValidationLoader
            fileName={addMultipleSPoCsStore.fileName}
            t={t}
            tokens={tokens}
            typography={typography}
            spacing={spacing}
          />
        ) : (
          <>
            <SPoCsUploadPane
              navigateTo={navigateTo}
              t={t}
              spacing={spacing}
              typography={typography}
              tokens={tokens}
              addMultipleSPoCsStore={addMultipleSPoCsStore}
            />
          </>
        )}
      </Stack>
    );
  },
);
